import React, { useCallback, useContext, useState } from 'react';
import { Button, Grid, Box, FormControlLabel, Checkbox } from '@mui/material';
import classNames from 'classnames';
import { useSnackbar } from 'notistack';
import printer from 'assets/icons/printer.svg';
import CredentialService from 'services/api/CredentialService';
import { errorMessage } from 'util/Request';
import commonStyles from 'styles/common.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { defaultFormProps } from 'util/Form';
import { ReportContext } from '../../reports/ReportsPage/context';
import { UserContext } from 'components/UserGuard';
import Modal from 'components/shared/Modal';
import { ActionButtons, DateRangeInput, TextInput, SelectInput } from '../../reports/forms/components';

interface PrintPipelineTranscriptButtonProps {
  providedNccerNumber: string;
  isFullWidth?: boolean;
  setLoading: (flag: boolean) => void;
  loading: boolean;
  className?: string;
}

type Values = {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
  fileFormat: 'csv' | 'pdf';
};

const defaultValues: Values = {
  startDate: null,
  endDate: null,
  fileFormat: 'pdf'
};

const PrintPipelineTranscriptButton: React.FunctionComponent<PrintPipelineTranscriptButtonProps> = ({
  providedNccerNumber,
  setLoading,
  loading,
  isFullWidth = true,
  className = '',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [includePartials, setIncludePartials] = useState(true);

  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);
  const form = useForm<Values>({ ...defaultFormProps, defaultValues });
  const { formState, getValues, handleSubmit } = form;

  const handleFormSubmit = useCallback(
    async (data: Values) => {
      setLoading(true);
      try {
        CredentialService.getTranscriptPipelineLink(providedNccerNumber, data.startDate, data.endDate, includePartials, data.fileFormat)
        .then((response) => {
          setLoading(false);
          window.location.assign(response);
        }).catch((error) => {
          setLoading(false);
          enqueueSnackbar(error.body, { variant: 'error' });
        });
        
      } finally {
        closeModal();
        setLoading(false);
      }
    },
    [providedNccerNumber, includePartials, closeModal, enqueueSnackbar, setLoading]
  );

  return (
    <>
      <Button
        variant={'contained'}
        color={'primary'}
        onClick={openModal}
        className={classNames(commonStyles.printTranscriptButton, className)}
        disabled={loading}
        fullWidth={isFullWidth}
        sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}
      >
        <img src={printer} className={commonStyles.buttonIcon} />{'View/Print Transcript'}
      </Button>
      <Modal loading={loading} open={open} title={"Pipeline Transcript"} subTitleText={"Generate a Pipeline Transcript with optional filters."} onClose={closeModal}>
        <FormProvider {...form}>
          <Grid container spacing={4} pb={6}>
            <DateRangeInput nameStartDate='startDate' nameEndDate='endDate' isRequired={false} subtitle=' - (Leave blank for report to include all Pipeline Training Completions)'/>
            <SelectInput name='fileFormat' label='File Format' options={[{value: 'pdf', label: "PDF"}, {value: 'csv', label:"CSV (Excel)"}]}/>
            <FormControlLabel style={{"paddingLeft" : 32, "paddingTop" : 16}}
              label={'Include Partial Pipeline Completions? Deselect for only Module Completions.'}
              control={<Checkbox checked={includePartials} onChange={(e) => setIncludePartials(e.target.checked)}/>}
            />
          </Grid>
          <ActionButtons
            submitText="Generate File"
            onClose={closeModal}
            onSubmit={handleSubmit(handleFormSubmit)}
          />
        </FormProvider>
      </Modal>
    </>
  );
};

export default PrintPipelineTranscriptButton;
